/**
 * summary.service.ts
 *
 * Maintains lightweight aggregate documents in Firestore so that
 * the dashboard and list pages can show counts without scanning
 * entire collections.
 *
 * Documents written:
 *   /analytics/studentsSummary   → {total, active, inactive, graduated, updatedAt}
 *   /analytics/teachersSummary   → {total, active, inactive, resigned, updatedAt}
 *   /analytics/staffSummary      → {total, active, inactive, resigned, updatedAt}
 */

import { db } from '../firebase/config';
import {
    collection,
    getDocs,
    doc,
    setDoc,
    serverTimestamp,
    onSnapshot,
    type Unsubscribe,
} from 'firebase/firestore';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface StudentsSummary {
    total: number;
    active: number;
    inactive: number;
    graduated: number;
    updatedAt?: any;
}

export interface TeachersSummary {
    total: number;
    active: number;
    inactive: number;
    resigned: number;
    updatedAt?: any;
}

export interface StaffSummary {
    total: number;
    active: number;
    inactive: number;
    resigned: number;
    updatedAt?: any;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const analyticsRef = (id: string) => doc(db, 'analytics', id);

// ─── Recalculate & write functions ─────────────────────────────────────────

/** Scan /students and write fresh counts to /analytics/studentsSummary */
export const recalculateStudentsSummary = async (): Promise<StudentsSummary> => {
    const snap = await getDocs(collection(db, 'students'));
    let active = 0, inactive = 0, graduated = 0;
    snap.forEach(d => {
        const status = d.data()?.academicInfo?.status ?? 'active';
        if (status === 'active') active++;
        else if (status === 'inactive') inactive++;
        else if (status === 'graduated') graduated++;
    });
    const summary: StudentsSummary = {
        total: snap.size,
        active,
        inactive,
        graduated,
        updatedAt: serverTimestamp(),
    };
    await setDoc(analyticsRef('studentsSummary'), summary);
    return summary;
};

/** Scan /teachers and write fresh counts to /analytics/teachersSummary */
export const recalculateTeachersSummary = async (): Promise<TeachersSummary> => {
    const snap = await getDocs(collection(db, 'teachers'));
    let active = 0, inactive = 0, resigned = 0;
    snap.forEach(d => {
        const status = d.data()?.currentPosition?.status ?? 'active';
        if (status === 'active') active++;
        else if (status === 'inactive') inactive++;
        else if (status === 'resigned') resigned++;
    });
    const summary: TeachersSummary = {
        total: snap.size,
        active,
        inactive,
        resigned,
        updatedAt: serverTimestamp(),
    };
    await setDoc(analyticsRef('teachersSummary'), summary);
    return summary;
};

/** Scan /staff and write fresh counts to /analytics/staffSummary */
export const recalculateStaffSummary = async (): Promise<StaffSummary> => {
    const snap = await getDocs(collection(db, 'staff'));
    let active = 0, inactive = 0, resigned = 0;
    snap.forEach(d => {
        const status = d.data()?.currentPosition?.status ?? 'active';
        if (status === 'active') active++;
        else if (status === 'inactive') inactive++;
        else if (status === 'resigned') resigned++;
    });
    const summary: StaffSummary = {
        total: snap.size,
        active,
        inactive,
        resigned,
        updatedAt: serverTimestamp(),
    };
    await setDoc(analyticsRef('staffSummary'), summary);
    return summary;
};

// ─── Real-time listeners ───────────────────────────────────────────────────

export const onStudentsSummary = (
    cb: (s: StudentsSummary) => void
): Unsubscribe =>
    onSnapshot(analyticsRef('studentsSummary'), snap => {
        if (snap.exists()) cb(snap.data() as StudentsSummary);
        else cb({ total: 0, active: 0, inactive: 0, graduated: 0 });
    });

export const onTeachersSummary = (
    cb: (s: TeachersSummary) => void
): Unsubscribe =>
    onSnapshot(analyticsRef('teachersSummary'), snap => {
        if (snap.exists()) cb(snap.data() as TeachersSummary);
        else cb({ total: 0, active: 0, inactive: 0, resigned: 0 });
    });

export const onStaffSummary = (
    cb: (s: StaffSummary) => void
): Unsubscribe =>
    onSnapshot(analyticsRef('staffSummary'), snap => {
        if (snap.exists()) cb(snap.data() as StaffSummary);
        else cb({ total: 0, active: 0, inactive: 0, resigned: 0 });
    });
